import { Trash, ArrowUp, ArrowDown, GearSix } from '@phosphor-icons/react';

const ACTION_LABELS = {
  check_bills_due: 'Check Bills Due',
  check_calendar: 'Check Calendar',
  scan_gmail: 'Scan Gmail',
  read_whatsapp_chat: 'Read WhatsApp Chat',
  extract_tasks_from_chat: 'Extract Tasks from Chat',
  generate_summary: 'Generate Summary',
  send_whatsapp: 'Send WhatsApp',
  send_notification: 'Send Notification',
  check_subscriptions: 'Check Subscriptions',
  create_task: 'Create Task',
  track_progress: 'Track Progress',
  web_search: 'Web Search (news / jobs / current info)',
};

const PARAM_FIELDS = {
  check_bills_due: [{ key: 'days_ahead', label: 'Days ahead', type: 'number', placeholder: '3' }],
  scan_gmail: [
    { key: 'query', label: 'Gmail search query', type: 'text', placeholder: 'e.g. subject:interview newer_than:7d' },
    { key: 'max_results', label: 'Max emails', type: 'number', placeholder: '10' },
  ],
  read_whatsapp_chat: [
    { key: 'contact', label: 'Contact/Group name', type: 'text', placeholder: 'e.g. Team Chat' },
    { key: 'limit', label: 'Messages to read', type: 'number', placeholder: '50' },
  ],
  extract_tasks_from_chat: [
    { key: 'auto_create_tasks', label: 'Auto-create tasks in inbox', type: 'select', options: ['true', 'false'] },
  ],
  generate_summary: [
    { key: 'template', label: 'Template', type: 'select', options: ['daily_briefing', 'team_update', 'subscription_review', 'bill_reminder'] },
    { key: 'length', label: 'Length', type: 'select', options: ['standard', 'short'] },
    { key: 'focus', label: 'Custom focus (optional — overrides template)', type: 'text', placeholder: 'e.g. job applications, interviews, follow-ups' },
  ],
  send_whatsapp: [
    { key: 'phone', label: 'Phone (with country code)', type: 'text', placeholder: '61412345678' },
    { key: 'group', label: 'Or Group name (instead of phone)', type: 'text', placeholder: 'e.g. Family, Work Team' },
    { key: 'message', label: 'Message (use {{summary}} for AI text)', type: 'textarea', placeholder: 'Good morning!\n\n{{summary}}' },
  ],
  send_notification: [
    { key: 'title', label: 'Title', type: 'text', placeholder: 'LifeAdmin Alert' },
    { key: 'body', label: 'Body (use {{summary}})', type: 'textarea', placeholder: '{{summary}}' },
  ],
  create_task: [
    { key: 'title', label: 'Task title', type: 'text', placeholder: 'Follow up on...' },
    { key: 'category', label: 'Category', type: 'select', options: ['task', 'bill', 'appointment'] },
  ],
  web_search: [
    { key: 'query', label: 'Search query (what to fetch)', type: 'text', placeholder: 'e.g. Top 3 AI news headlines today', required: true },
    { key: 'topic', label: 'Topic label (news / jobs / weather)', type: 'text', placeholder: 'news' },
    { key: 'country', label: 'Country / region (optional)', type: 'text', placeholder: 'e.g. Australia' },
    { key: 'max_results', label: 'Max results', type: 'number', placeholder: '3' },
  ],
};

export const ActionStepEditor = ({ action, index, total, onChange, onRemove, onMoveUp, onMoveDown }) => {
  const paramDefs = PARAM_FIELDS[action.type] || [];
  const params = action.params || {};

  const updateParam = (key, value) => {
    onChange({ ...action, params: { ...params, [key]: value } });
  };

  return (
    <div className="rounded-lg border border-[#e2e8f0] bg-white overflow-hidden" data-testid={`action-step-${index}`}>
      {/* Step header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-[#fafbfc] border-b border-[#f0f1f3]">
        <span className="w-5 h-5 rounded-full bg-[#0f172a] text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">
          {index + 1}
        </span>

        <select
          value={action.type}
          onChange={(e) => onChange({ ...action, type: e.target.value, name: ACTION_LABELS[e.target.value] || e.target.value })}
          className="flex-1 text-xs font-semibold bg-transparent border-none outline-none text-[#2d3338] cursor-pointer"
          data-testid={`action-type-${index}`}
        >
          {/* Keep the current type visible even if it's not in our known list
              (e.g. new step types added by the assistant). */}
          {!ACTION_LABELS[action.type] && action.type && (
            <option value={action.type}>{action.name || action.type}</option>
          )}
          {Object.entries(ACTION_LABELS).map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>

        <div className="flex items-center gap-0.5">
          {index > 0 && (
            <button onClick={onMoveUp} className="p-1 rounded hover:bg-[#e2e8f0] text-[#acb3b8]"><ArrowUp size={12} /></button>
          )}
          {index < total - 1 && (
            <button onClick={onMoveDown} className="p-1 rounded hover:bg-[#e2e8f0] text-[#acb3b8]"><ArrowDown size={12} /></button>
          )}
          <button onClick={onRemove} className="p-1 rounded hover:bg-[#fee2e2] text-[#acb3b8] hover:text-[#dc2626]"><Trash size={12} /></button>
        </div>
      </div>

      {/* Params */}
      {paramDefs.length > 0 && (
        <div className="p-3 space-y-2">
          {paramDefs.map((p) => {
            const isEmpty = p.required && !params[p.key];
            return (
            <div key={p.key}>
              <label className="text-[10px] font-medium text-[#596065] block mb-0.5">
                {p.label}{p.required && <span className="text-[#dc2626] ml-0.5">*</span>}
              </label>
              {p.type === 'textarea' ? (
                <textarea
                  value={params[p.key] || ''}
                  onChange={(e) => updateParam(p.key, e.target.value)}
                  placeholder={p.placeholder}
                  rows={2}
                  className={`w-full text-xs border rounded-lg px-2.5 py-1.5 outline-none focus:border-[#005bc1] resize-none ${isEmpty ? 'border-[#f59e0b] bg-[#fffbeb]' : 'border-[#e2e8f0]'}`}
                />
              ) : p.type === 'select' ? (
                <select
                  value={params[p.key] || p.options[0]}
                  onChange={(e) => updateParam(p.key, e.target.value)}
                  className="w-full text-xs border border-[#e2e8f0] rounded-lg px-2.5 py-1.5 outline-none focus:border-[#005bc1]"
                >
                  {p.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input
                  type={p.type}
                  value={params[p.key] || ''}
                  onChange={(e) => updateParam(p.key, p.type === 'number' ? Number(e.target.value) : e.target.value)}
                  placeholder={p.placeholder}
                  className={`w-full text-xs border rounded-lg px-2.5 py-1.5 outline-none focus:border-[#005bc1] ${isEmpty ? 'border-[#f59e0b] bg-[#fffbeb]' : 'border-[#e2e8f0]'}`}
                />
              )}
              {isEmpty && <p className="text-[9px] text-[#f59e0b] mt-0.5">Required — routine will fail without this</p>}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
